import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/api/axiosApi'
import { queryKeys } from '@/lib/queryKeys'
import type { ApiPlanDetailResponse } from '@/types'
import { useCurrentVersionStore } from '@/stores/useCurrentVersionStore'

/**
 * Fetches and caches the active_version_id for a plan.
 * Use this hook in mutation hooks to avoid redundant API calls.
 *
 * The version ID is cached for 5 minutes by default.
 */
export function useActiveVersionId(planId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.planActiveVersion.byId(planId!),
    queryFn: async () => {
      const response = await api.get<ApiPlanDetailResponse>(`/plans/${planId}`)
      return response.data.active_version_id
    },
    enabled: !!planId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  })
}

/**
 * Helper to get the active version ID synchronously from cache.
 * Returns undefined if not cached.
 *
 * Usage in mutations:
 * ```ts
 * const queryClient = useQueryClient()
 * const versionId = getActiveVersionIdFromCache(queryClient, planId)
 * ```
 */
export function getActiveVersionIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  planId: string
): string | null | undefined {
  return queryClient.getQueryData<string | null>(
    queryKeys.planActiveVersion.byId(planId)
  )
}

/**
 * Fetches active version ID, using cache if available.
 * Falls back to API call if not cached.
 *
 * This is the recommended way to get version ID in mutation functions.
 */
export async function getActiveVersionId(
  queryClient: ReturnType<typeof useQueryClient>,
  planId: string
): Promise<string> {
  // Try to get from cache first
  const cached = getActiveVersionIdFromCache(queryClient, planId)
  if (cached) {
    return cached
  }

  // Fetch from API and cache it
  const response = await api.get<ApiPlanDetailResponse>(`/plans/${planId}`)
  const activeVersionId = response.data.active_version_id

  if (!activeVersionId) {
    throw new Error('No active version found for this plan')
  }

  // Store in cache for future use
  queryClient.setQueryData(
    queryKeys.planActiveVersion.byId(planId),
    activeVersionId
  )

  return activeVersionId
}

/**
 * Returns the effective version ID for use in mutations.
 * Checks the Zustand store first (sync), falls back to getActiveVersionId.
 */
export async function getEffectiveVersionId(
  queryClient: ReturnType<typeof useQueryClient>,
  planId: string
): Promise<string> {
  const storedVersionId =
    useCurrentVersionStore.getState().versionsByPlan[planId]
  if (storedVersionId) {
    return storedVersionId
  }
  return getActiveVersionId(queryClient, planId)
}
